// Recovery for "failed to load a code-split chunk" errors. Every route in this
// app is lazy-loaded via dynamic import(), so a chunk that fails to load breaks
// (or blanks) the page. WebKit/Safari reports this as
// `TypeError: Importing a module script failed.`, Chromium as
// `Failed to fetch dynamically imported module`, Firefox as
// `error loading dynamically imported module`.
//
// It almost always means one of:
//   - the HTML references chunk hashes from a build a newer deploy has replaced
//     (a stale asset — common right after every deploy), or
//   - a transient network drop (common on mobile Safari / Chrome iOS).
//
// In both cases the fix is the same: reload once to fetch the current build.
// Guarded so a genuinely broken build or a dead network can't trap the user in a
// reload loop.

const RELOAD_MARKER_KEY = 'kindlumen:chunk-reload-at'
// Fallback loop guard for when storage is unavailable. Safari and other
// browsers throw on sessionStorage inside third-party iframes — exactly where
// donation forms embedded on organizations' websites run — so the reload time
// travels in the URL instead.
const RELOAD_MARKER_PARAM = 'kl_reload'
const RELOAD_COOLDOWN_MS = 20_000

// A reload already started from this document (several listeners can report
// the same failure before the navigation happens).
let reloadRequested = false

const CHUNK_ERROR_PATTERN =
  /importing a module script failed|failed to fetch dynamically imported module|error loading dynamically imported module|expected a javascript(?:-or-wasm)? module script|is not a valid javascript mime type/i

export function isChunkLoadError(reason: unknown): boolean {
  if (!reason) return false
  const message =
    typeof reason === 'string'
      ? reason
      : reason instanceof Error
        ? reason.message
        : typeof (reason as { message?: unknown }).message === 'string'
          ? (reason as { message: string }).message
          : ''
  return CHUNK_ERROR_PATTERN.test(message)
}

function reloadedRecently(timestamp: number) {
  return Number.isFinite(timestamp) && Date.now() - timestamp < RELOAD_COOLDOWN_MS
}

// Reload once to recover, unless we already reloaded very recently — in which
// case the reload didn't help (broken build / dead network), so let the error
// surface to the error boundary instead of looping. Returns true if a reload
// was triggered.
export function recoverFromChunkError(): boolean {
  if (typeof window === 'undefined') return false
  if (reloadRequested) return true

  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_MARKER_KEY) ?? '0')
    if (reloadedRecently(last)) return false

    window.sessionStorage.setItem(RELOAD_MARKER_KEY, String(Date.now()))
  } catch {
    // Storage is blocked (third-party iframe, private mode, quota). Without a
    // reload, an embedded form that lost one chunk stays blank for good, so
    // carry the marker in the URL to keep the one-reload limit.
    return reloadWithUrlMarker()
  }
  reloadRequested = true
  window.location.reload()
  return true
}

function reloadWithUrlMarker(): boolean {
  try {
    const url = new URL(window.location.href)
    if (reloadedRecently(Number(url.searchParams.get(RELOAD_MARKER_PARAM) ?? '0'))) return false

    url.searchParams.set(RELOAD_MARKER_PARAM, String(Date.now()))
    reloadRequested = true
    window.location.replace(url.toString())
    return true
  } catch {
    return false
  }
}

// Install global listeners that recover from chunk-load failures. Called once at
// boot; a no-op during SSG prerender (no window).
export function installChunkErrorRecovery(): void {
  if (typeof window === 'undefined') return

  // Vite dispatches this on window when its dynamic-import preload helper fails.
  // The event name isn't in the DOM lib's WindowEventMap, so bind through a
  // loosened signature to stay compatible across @types/vite versions.
  const addWindowListener = window.addEventListener.bind(window) as (
    type: string,
    listener: (event: Event) => void,
  ) => void
  addWindowListener('vite:preloadError', () => {
    recoverFromChunkError()
  })

  // Belt-and-suspenders: the observed failure arrived as an unhandled rejection
  // ("Importing a module script failed."), so match that shape too.
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) {
      recoverFromChunkError()
    }
  })
}
